import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import introVideo from "@/assets/realv3-intro.mp4";


const VideoSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();

  const stats = [
    { num: "0.50%", label: "Flat fee at closing" },
    { num: "$22k+", label: "Average saved" },
    { num: "100%", label: "Concierge guided" },
    { num: "$0", label: "Agent commission" },
  ];

  return (
    <section className="relative bg-canvas py-16 md:py-20 px-8 md:px-12 overflow-hidden">
      {/* ambient warmth */}
      <div className="pointer-events-none absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-gold/8 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 left-0 w-[450px] h-[450px] rounded-full bg-wood/10 blur-3xl" />

      <div className="relative max-w-[960px] mx-auto">
        {/* Two-column: copy on left, phone on right */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-12 items-center">
          {/* Left — Section header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, margin: "-10%" }}
            className="max-w-2xl"
          >
            <div className="inline-flex items-center gap-2.5 mb-6">
              <span className="w-8 h-px bg-gold" />
              <span className="text-[11px] font-medium tracking-[0.25em] uppercase text-ink-soft">
                See it in motion
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl font-light text-ink leading-[1.0] tracking-tight mb-6">
              Watch how it
              <br />
              <span className="accent-italic text-wood-deep">actually works.</span>
            </h2>
            <p className="text-base md:text-lg text-ink-soft leading-[1.7] font-light max-w-xl">
              No gimmicks. No hidden fees. A short tour through listing, showings, offers, and closing — guided every step of the way.
            </p>
          </motion.div>

          {/* Right — iPhone mockup */}
          <div className="flex justify-center lg:justify-start lg:pr-16 xl:pr-24">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true, margin: "-10%" }}
              className="relative"
            >
              {/* iPhone frame */}
              <div className="relative w-[200px] sm:w-[240px] md:w-[300px]">
                {/* Outer phone body */}
                <div
                  className="relative aspect-[9/19.5] rounded-[48px] bg-ink p-[10px] shadow-deep"
                  style={{
                    boxShadow:
                      "0 50px 100px -20px hsl(var(--ink) / 0.35), 0 30px 60px -30px hsl(var(--ink) / 0.4), inset 0 0 0 2px hsl(var(--ink-soft) / 0.3)",
                  }}
                >
                  {/* Inner screen */}
                  <div className="relative w-full h-full rounded-[40px] overflow-hidden bg-ink">
                    {/* Notch / Dynamic Island */}
                    <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-20 w-[90px] h-[26px] bg-ink rounded-full" />

                    {!isPlaying ? (
                      <button
                        onClick={() => {
                          setIsPlaying(true);
                          setTimeout(() => videoRef.current?.play(), 0);
                        }}
                        className="absolute inset-0 group cursor-pointer"
                        aria-label="Play introduction video"
                      >
                        <video
                          src={introVideo}
                          muted
                          playsInline
                          preload="metadata"
                          className="absolute inset-0 w-full h-full object-cover scale-[1.15] object-top"
                        />
                        <div className="absolute inset-0 bg-ink/40 group-hover:bg-ink/30 transition-colors duration-500" />

                        <div className="relative h-full flex flex-col items-center justify-center gap-5 px-6">
                          <motion.div
                            whileHover={{ scale: 1.08 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="w-16 h-16 rounded-full bg-gold flex items-center justify-center shadow-lift"
                          >
                            <Play className="h-6 w-6 text-ink ml-0.5" fill="currentColor" />
                          </motion.div>
                          <div className="text-center text-canvas">
                            <p className="text-sm font-medium tracking-wide">Watch introduction</p>
                            <p className="text-[10px] text-canvas/65 mt-1.5 uppercase tracking-[0.2em]">
                              2 min · sound on
                            </p>
                          </div>
                        </div>
                      </button>
                    ) : (
                      <video
                        ref={videoRef}
                        src={introVideo}
                        controls
                        autoPlay
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover bg-ink scale-[1.15] object-top"
                      />
                    )}
                  </div>
                </div>

              </div>
            </motion.div>
          </div>
        </div>

        {/* Editorial stats — hairline divided */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-10%" }}
          className="mt-14 pt-8 border-t border-ink/10 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
            >
              <div className="font-display text-4xl md:text-5xl font-light text-ink tabular-nums leading-none mb-2">
                {stat.num}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-soft font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-10%" }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-8 border-t border-ink/10"
        >
          <p className="font-display text-2xl md:text-3xl font-light text-ink tracking-tight max-w-md">
            Ready to keep <span className="accent-italic text-wood-deep">what's yours?</span>
          </p>
          <div className="flex flex-wrap gap-3 sm:ml-auto">
            <Button
              size="lg"
              onClick={() => navigate("/auth")}
              className="bg-gold hover:bg-gold-deep text-ink font-medium px-7 py-6 text-sm tracking-wide rounded-md shadow-soft hover:shadow-lift transition-all duration-500"
            >
              Join REALV3
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate("/about")}
              className="border border-ink/15 bg-transparent hover:bg-ink hover:text-canvas hover:border-ink text-ink font-medium px-7 py-6 text-sm tracking-wide rounded-md transition-all duration-500"
            >
              Learn more
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VideoSection;
